import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import DOMPurify from 'dompurify';
import { useSnackbar } from 'notistack';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import * as Answers from '../../api/ventures/quarterlyrecords/customanswers';
import {
  Answer,
  AnswerId,
} from '../../api/ventures/quarterlyrecords/customanswers/Answer';
import { VentureQuarterRecord } from '../../api/ventures/quarterlyrecords/types/VentureQuarterRecord';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { AlertState, FormGroup, Text } from '../common';
import { Delete } from '../common/ActionIcons/Delete';
import { Loading } from '../common/ActionIcons/Loading';
import { RemoveAnswerModal } from '../common/remove-answer-modal';
import { FormInitialValues } from '../forms/report-venture-quarter-form';
import { FormRating, TextFieldSummary } from '../forms/wrappers';

export interface Props {
  report: VentureQuarterRecord;
}

const useStyles = makeStyles(() => ({
  formBlocks: {
    display: 'flex',
    alignItems: 'flex-start',
  },

  formContainer: {
    width: '100%',
  },
  ul_list: {
    '& ul, li': {
      listStyle: 'unset',
    },
  },
  formButtons: {
    justifyContent: 'flex-start',
  },
  alert: {
    margin: '20px 0',
  },
  title: {
    margin: '20px 0',
  },
  subtitle: {
    margin: '20px 0 10px',
  },
  fields_line: {
    display: 'flex',
    alignItems: 'center',

    '& + &': {
      marginTop: 20,
    },
  },
  field_amount: {
    width: '33%',
  },
  field_comment: {
    width: 'calc(67% - 20px)',
    marginLeft: 20,
  },
  questionItem: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
  },
  questionItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
}));

const getInitialValues = (details: VentureQuarterRecord): FormInitialValues => {
  let rating = details.rating;

  if (details.status === 'SUBMITTED' && typeof rating === 'number') {
    rating = rating + 1;
  }

  const getValue = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value);
  };

  return {
    ventureName: details.ventureName || '',
    submitter: getValue(details.submitter),
    fte: getValue(details.fte),
    revenue: getValue(details.revenue),
    equityRaised: getValue(details.equityRaised),
    equityComments: getValue(details.equityComments),
    grantsRaised: getValue(details.grantsRaised),
    grantsComments: getValue(details.grantsComments),
    crowdFunding: getValue(details.crowdFunding),
    crowdComments: getValue(details.crowdComments),
    loans: getValue(details.loans),
    loansComments: getValue(details.loansComments),
    other: getValue(details.other),
    otherComments: getValue(details.otherComments),
    rating: getValue(rating),
    questions: {},
  };
};

const getMonthsByQuarter = (quarter: number) => {
  switch (quarter) {
    case 1: {
      return '(Jan, Feb, March)';
    }
    case 2: {
      return '(April, May, June)';
    }
    case 3: {
      return '(July, August, Sept)';
    }
    default: {
      return '(Oct, Nov, Dec)';
    }
  }
};

function AnswerElement({
  answer,
  index,
  onRemove,
}: {
  answer: Answer;
  index: number;
  onRemove: (id: AnswerId) => Promise<void>;
}) {
  const classes = useStyles();

  const [showRemove, setShowRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = useCallback(async () => {
    try {
      setIsRemoving(true);
      await onRemove(answer.id);
    } catch (error) {
      throw error;
    } finally {
      setIsRemoving(false);
      setShowRemove(false);
    }
  }, [answer.id, onRemove]);

  return (
    <div
      key={answer.id}
      data-testid='question-item'
      className={classes.questionItem}>
      <div className={classes.questionItemHeader}>
        <Typography variant='h5'>
          {index + 5}. {answer.description}
        </Typography>
        {isRemoving ? (
          <Loading />
        ) : (
          <Delete
            onClick={() => {
              setShowRemove(true);
            }}
          />
        )}
      </div>
      <FormGroup>
        <Text variant={'normal'}>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(answer.answer),
            }}
          />
        </Text>
      </FormGroup>
      <RemoveAnswerModal
        open={showRemove}
        isLoading={isRemoving}
        onRemove={handleRemove}
        onClose={() => setShowRemove(false)}
      />
    </div>
  );
}

export function ReportItem({ report }: Props): ReactElement {
  const classes = useStyles();
  const { rb } = useResourceBundles();
  const { enqueueSnackbar } = useSnackbar();
  const initialValues = useMemo(() => getInitialValues(report), [report]);

  const [answers, setAnswers] = useState<Answer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getAnswers = async () => {
      try {
        setIsLoading(true);
        const answers = await Answers.get(report.id);
        setAnswers(answers);
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Failed to get answers', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    getAnswers();
  }, [enqueueSnackbar, report.id]);

  const handleRemoveAnswer = useCallback(
    async (id: AnswerId) => {
      try {
        await Answers.remove(report.id, id);
        setAnswers((prev) => prev.filter((answer) => answer.id !== id));
        enqueueSnackbar('Answer was removed succesfully', {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);
        enqueueSnackbar('Failed to remove answer', { variant: 'error' });
      }
    },
    [enqueueSnackbar, report.id],
  );

  if (isLoading) {
    return <CircularProgress size={24} color='inherit' />;
  }

  return (
    <Form
      onSubmit={() => {}}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      render={() => (
        <div className={classes.formBlocks}>
          <form noValidate className={classes.formContainer}>
            <FormGroup>
              <Field<string>
                name='ventureName'
                component={TextFieldSummary}
                label='Venture'
                disabled
              />
            </FormGroup>
            <Typography className={classes.title} variant='h4'>
              Q{report.quarterNumber} Report {report.year}{' '}
              {getMonthsByQuarter(report.quarterNumber)}
            </Typography>
            <FormGroup>
              <Field<string>
                name='submitter'
                component={TextFieldSummary}
                label='Email*'
                InputProps={{
                  inputProps: {
                    disabled: true,
                  },
                }}
              />
            </FormGroup>
            <FormGroup>
              <Typography variant='h5'>
                1. What is your Full Time Employee Equivalent number? Your FTE
                number = (Total average of part-time hours worked per week / 30)
                + # of full-time employees
              </Typography>
            </FormGroup>
            <AlertState className={classes.alert} type='info'>
              <Text className={classes.ul_list} variant='normal'>
                It’s pretty easy. Grab a pencil, open your notebook, and we’ll
                show you exactly how to calculate your FTE count.
                <br />
                <br />
                <ul>
                  <li>
                    <b>Step 1:</b> How many part-time workers do you have?{' '}
                    <i>
                      According to the IRS, part-timers are those who work less
                      than 130 hours a month, or 30 hours a week.
                    </i>
                  </li>
                  <li>
                    <b>Step 2:</b> Write down the average number of hours each
                    part-time employee works per week. Now, add them all
                    together.
                  </li>
                  <li>
                    <b>Step 3:</b> Take the result and divide it by 30.
                  </li>
                  <li>
                    <b>Step 4:</b> Round down to the nearest whole number.
                  </li>
                </ul>
              </Text>
            </AlertState>
            <FormGroup>
              <Field<string>
                name='fte'
                component={TextFieldSummary}
                label='Number of Full-Time Employees*'
                format={(val) =>
                  val
                    ? val
                        // Allow only numbers
                        .replace(/[^0-9]+/g, '')
                        // Prevent number starting with 0
                        .replace(/^0(?=\d)/, '')
                        // Number mus be less then 10.000.000
                        .slice(0, 7)
                    : val
                }
                InputProps={{
                  inputProps: {
                    disabled: true,
                  },
                }}
              />
            </FormGroup>
            <Typography className={classes.subtitle} variant='h5'>
              2. What was your total revenue in the last quarter?
            </Typography>
            <FormGroup>
              <Field<string>
                name='revenue'
                component={TextFieldSummary}
                label='Revenue last quarter*'
                format={(val = '') =>
                  val
                    // Allow only numbers
                    .replace(/[^0-9]+/g, '')
                    // Prevent number starting with 0
                    .replace(/^0(?=\d)/, '')
                    // Number mus be less then 10.000.000
                    .slice(0, 7)
                }
                InputProps={{
                  inputProps: {
                    disabled: true,
                  },
                }}
              />
            </FormGroup>
            <Typography className={classes.subtitle} variant='h5'>
              3. Share funds raised or borrowed last quarter. Please fill out
              and comment on relevant fields
            </Typography>
            <div>
              <div className={classes.fields_line}>
                <div className={classes.field_amount}>
                  <Field<string>
                    name='equityRaised'
                    component={TextFieldSummary}
                    label='Amount of equity raised'
                    format={(val) => (val ? val.replace(/[^0-9]+/g, '') : val)}
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
                <div className={classes.field_comment}>
                  <Field<string>
                    name='equityComments'
                    component={TextFieldSummary}
                    label='Provide details'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
              </div>
              <div className={classes.fields_line}>
                <div className={classes.field_amount}>
                  <Field<string>
                    name='grantsRaised'
                    component={TextFieldSummary}
                    label='Amount of grants received'
                    format={(val) => (val ? val.replace(/[^0-9]+/g, '') : val)}
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
                <div className={classes.field_comment}>
                  <Field<string>
                    name='grantsComments'
                    component={TextFieldSummary}
                    label='Provide details'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
              </div>
              <div className={classes.fields_line}>
                <div className={classes.field_amount}>
                  <Field<string>
                    name='crowdFunding'
                    component={TextFieldSummary}
                    label='Amount of crowdfunding'
                    format={(val) => (val ? val.replace(/[^0-9]+/g, '') : val)}
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
                <div className={classes.field_comment}>
                  <Field<string>
                    name='crowdComments'
                    component={TextFieldSummary}
                    label='Provide details'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
              </div>
              <div className={classes.fields_line}>
                <div className={classes.field_amount}>
                  <Field<string>
                    name='loans'
                    component={TextFieldSummary}
                    label='Loans received'
                    format={(val) => (val ? val.replace(/[^0-9]+/g, '') : val)}
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
                <div className={classes.field_comment}>
                  <Field<string>
                    name='loansComments'
                    component={TextFieldSummary}
                    label='Provide details'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
              </div>
              <div className={classes.fields_line}>
                <div className={classes.field_amount}>
                  <Field<string>
                    name='other'
                    component={TextFieldSummary}
                    label='Other funding'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
                <div className={classes.field_comment}>
                  <Field<string>
                    name='otherComments'
                    component={TextFieldSummary}
                    label='Provide details'
                    InputProps={{
                      inputProps: {
                        disabled: true,
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <Typography className={classes.subtitle} variant='h5'>
              4. How likely are you to recommend our {rb('mentor')} program to a
              friend?
            </Typography>
            <FormGroup>
              <Field<number>
                name='rating'
                component={FormRating}
                max={11}
                readOnly={true}
                withCaption
              />
            </FormGroup>

            {answers.map((answer, i) => (
              <AnswerElement
                answer={answer}
                index={i}
                onRemove={handleRemoveAnswer}
              />
            ))}
          </form>
        </div>
      )}
    />
  );
}
